"use client";

/**
 * In-app browser (webview) detection.
 *
 * Social apps (Instagram, Facebook, TikTok, …) open links in an embedded
 * webview, not the system browser. Those webviews block things Hoku needs:
 * - Google OAuth login (Google refuses `disallowed_useragent`)
 * - getUserMedia camera access (NotAllowedError — see HOKU-FRONTEND-V2-7)
 * - Web push / notification permissions
 * - PWA "Add to Home Screen"
 *
 * So we detect the webview and nudge users to their real browser. Pure
 * functions here (no React/DOM side effects beyond reading the UA) so they
 * can be unit-tested in isolation.
 */

export type InAppBrowser =
  | "instagram"
  | "facebook"
  | "messenger"
  | "tiktok"
  | "snapchat"
  | "linkedin"
  | "twitter"
  | "other-webview"
  | null;

export type MobileOS = "ios" | "android" | "other";

/**
 * Classify the in-app browser from a user-agent string. Returns null when the
 * UA looks like a normal standalone browser (Chrome, Safari, Firefox, …).
 *
 * Order matters: the named apps are checked before the generic Android
 * `; wv` webview marker so we can show app-specific copy.
 */
export function detectInAppBrowser(ua: string | undefined | null): InAppBrowser {
  if (!ua) return null;

  // Named social webviews (most specific first).
  if (/Instagram/i.test(ua)) return "instagram";
  // Messenger and the FB app both carry FBAN/FBAV; Messenger adds FB_IAB/Messenger.
  if (/FBAN\/Messenger|Messenger/i.test(ua)) return "messenger";
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return "facebook";
  if (/TikTok|BytedanceWebview|musical_ly|Trill\//i.test(ua)) return "tiktok";
  if (/Snapchat/i.test(ua)) return "snapchat";
  if (/LinkedInApp/i.test(ua)) return "linkedin";
  if (/Twitter|TwitterAndroid/i.test(ua)) return "twitter";

  // Generic Android WebView marker (e.g. ` wv) `). Standalone Chrome on
  // Android does NOT include "; wv", so this catches misc in-app browsers
  // without flagging the real browser.
  if (/Android/i.test(ua) && /;\s*wv[);]/i.test(ua)) return "other-webview";

  return null;
}

export function detectMobileOS(
  ua: string | undefined | null,
  platform?: string,
  maxTouchPoints?: number
): MobileOS {
  if (!ua) return "other";
  // iPadOS 13+ reports as "MacIntel" with touch points, not "iPad".
  const isIpadOS = platform === "MacIntel" && (maxTouchPoints ?? 0) > 1;
  if (/iPad|iPhone|iPod/i.test(ua) || isIpadOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

export interface InAppBrowserInfo {
  app: InAppBrowser;
  os: MobileOS;
  /** True when we should show the "open in real browser" gate. */
  isInApp: boolean;
}

/** Read the current environment's UA. Safe to call on the server (returns not-in-app). */
export function getInAppBrowserInfo(): InAppBrowserInfo {
  if (typeof navigator === "undefined") {
    return { app: null, os: "other", isInApp: false };
  }
  const ua = navigator.userAgent;
  const app = detectInAppBrowser(ua);
  const os = detectMobileOS(ua, navigator.platform, navigator.maxTouchPoints);
  return { app, os, isInApp: app !== null };
}

/**
 * Build an Android `intent://` URL that force-opens the given https URL in
 * Chrome, breaking out of the webview. iOS has no equivalent escape hatch,
 * so callers fall back to instructions there.
 */
export function buildAndroidChromeIntent(url: string): string {
  // Strip the scheme — intent:// carries the host/path, scheme goes in the
  // Intent fragment.
  const stripped = url.replace(/^https?:\/\//i, "");
  return `intent://${stripped}#Intent;scheme=https;package=com.android.chrome;end`;
}

/** Human-friendly app name for UI copy. */
export function inAppBrowserLabel(app: InAppBrowser): string {
  switch (app) {
    case "instagram":
      return "Instagram";
    case "facebook":
      return "Facebook";
    case "messenger":
      return "Messenger";
    case "tiktok":
      return "TikTok";
    case "snapchat":
      return "Snapchat";
    case "linkedin":
      return "LinkedIn";
    case "twitter":
      return "X";
    default:
      return "this app";
  }
}
